import { useEffect, useState } from "react";
import { PaperPlaneTilt, Trash, CheckCircle, User, Users, WarningCircle } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { toast } from "sonner";

export default function AdminKirimPesan() {
  const [title, setTitle] = useState("");
  const [bodyText, setBodyText] = useState("");
  const [target, setTarget] = useState("all");
  const [important, setImportant] = useState(false);
  const [teachers, setTeachers] = useState([]);
  const [msgs, setMsgs] = useState([]);
  const [sending, setSending] = useState(false);
  const [delId, setDelId] = useState(null);

  const loadMsgs = () => api.get("/admin/guru-messages").then((r) => setMsgs(r.data)).catch(() => {});
  useEffect(() => {
    api.get("/users?role=guru").then((r) => setTeachers(r.data)).catch(() => {});
    loadMsgs();
    api.post("/admin/guru-messages/seen").catch(() => {});
  }, []);

  const send = async () => {
    if (!title.trim() || !bodyText.trim()) { toast.error("Judul & pesan wajib diisi"); return; }
    setSending(true);
    try {
      await api.post("/admin/guru-messages", { title, body: bodyText, target, important });
      toast.success("Pesan terkirim");
      setTitle(""); setBodyText(""); setTarget("all"); setImportant(false);
      loadMsgs();
    } catch (e) { toast.error("Gagal mengirim"); } finally { setSending(false); }
  };

  const del = async (id) => {
    try { await api.delete(`/admin/guru-messages/${id}`); toast.success("Pesan dihapus"); setDelId(null); loadMsgs(); }
    catch (e) { toast.error("Gagal menghapus"); }
  };

  return (
    <div className="min-h-screen bg-stone-50 pb-24" data-testid="kirim-pesan-page">
      <PageHeader title="Kirim Pesan ke Guru" />
      <div className="px-4 pt-4 space-y-4">
        <div className="bg-white rounded-3xl border border-stone-200/60 shadow-sm p-4 space-y-3">
          <div>
            <label className="text-xs font-semibold text-stone-600">Kirim ke</label>
            <select data-testid="pesan-target" value={target} onChange={(e) => setTarget(e.target.value)}
              className="w-full mt-1 px-3 py-2.5 rounded-xl border border-stone-200 text-sm bg-white focus:outline-none focus:border-primary">
              <option value="all">Semua Guru</option>
              {teachers.map((t) => <option key={t.id} value={t.id}>{t.name}{t.kelas ? ` — ${t.kelas}` : ""}</option>)}
            </select>
          </div>
          <input data-testid="pesan-judul" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Judul"
            className="w-full px-3 py-2.5 rounded-xl border border-stone-200 text-sm font-bold focus:outline-none focus:border-primary" />
          <textarea data-testid="pesan-body" value={bodyText} onChange={(e) => setBodyText(e.target.value)} rows={4} placeholder="Tulis pesan..."
            className="w-full px-3 py-2.5 rounded-xl border border-stone-200 text-sm focus:outline-none focus:border-primary resize-none" />
          <label className="flex items-start gap-2 text-sm text-stone-700 cursor-pointer select-none">
            <input data-testid="pesan-penting" type="checkbox" checked={important} onChange={(e) => setImportant(e.target.checked)} className="w-4 h-4 mt-0.5 accent-red-500" />
            <span className="flex items-center gap-1 leading-tight"><WarningCircle size={15} weight="fill" className="text-red-500 shrink-0" /> Tandai Penting</span>
          </label>
          <button data-testid="pesan-kirim" disabled={sending} onClick={send}
            className="w-full bg-primary text-white font-semibold py-3 rounded-full text-sm flex items-center justify-center gap-2 active:scale-95 transition-transform disabled:opacity-60">
            <PaperPlaneTilt size={18} weight="bold" /> {sending ? "Mengirim..." : "Kirim Pesan"}
          </button>
        </div>

        <div>
          <h3 className="font-heading font-bold text-stone-800 text-sm mb-2">Arsip Pesan</h3>
          {msgs.length === 0 && <p className="text-center text-stone-400 py-8 text-sm">Belum ada pesan.</p>}
          <div className="space-y-3">
            {msgs.map((m) => (
              <div key={m.id} data-testid={`pesan-arsip-${m.id}`} className="bg-white rounded-2xl border border-stone-200/60 shadow-sm p-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-stone-800 text-sm">{m.title}{m.important && <span className="ml-1.5 text-[9px] font-bold bg-red-100 text-red-600 px-1.5 py-0.5 rounded-full align-middle">PENTING</span>}</p>
                    <p className="text-sm text-stone-600 whitespace-pre-wrap mt-0.5">{m.body}</p>
                    <p className="text-[11px] text-stone-400 mt-1.5 flex items-center gap-1">
                      {m.target === "all" ? <Users size={13} /> : <User size={13} />} {m.target_name}
                      {m.created_at ? ` · ${new Date(m.created_at).toLocaleString("id-ID", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}` : ""}
                    </p>
                  </div>
                  <button data-testid={`pesan-hapus-${m.id}`} onClick={() => setDelId(m.id)} className="text-stone-300 hover:text-red-500 shrink-0"><Trash size={18} /></button>
                </div>
                {m.responses.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-stone-100 space-y-1.5">
                    <p className="text-[11px] font-bold text-green-700 flex items-center gap-1"><CheckCircle size={14} weight="fill" /> Direspon {m.response_count} guru</p>
                    {m.responses.map((r, i) => (
                      <div key={i} className="text-xs text-stone-600">
                        <b className="text-stone-800">{r.teacher_name}</b>{r.kelas ? ` (${r.kelas})` : ""}: InsyaAllah{r.reply ? ` — "${r.reply}"` : ""}
                      </div>
                    ))}
                  </div>
                )}
                {delId === m.id && (
                  <div className="mt-3 flex gap-2">
                    <button data-testid={`pesan-hapus-ya-${m.id}`} onClick={() => del(m.id)} className="flex-1 bg-red-600 text-white text-xs font-semibold py-2 rounded-full">Hapus bersih</button>
                    <button onClick={() => setDelId(null)} className="flex-1 bg-stone-100 text-stone-600 text-xs font-semibold py-2 rounded-full">Batal</button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
